<template>
  <div>
    <doctors-table-filter v-model="filters"></doctors-table-filter>

    <result-number :number="itemsTotal"></result-number>

    <v-data-table
      dense
      disable-sort
      class="mt-5"
      :headers="headers"
      :items="innerVal"
      ref="doctorsTable"
      :footer-props="{
        'items-per-page-options': rowsPerPageItems
      }"
      :items-per-page="showAssignColumn?5:10"
    >
      <!-- custom row -->
      <template
        v-slot:body="{ items }"
      >
        <drop
          v-for="(item, index) in items"
          :key="index"
          tag="tbody"
          :class="{'selected primary lighten-3': assign.index == index}"
          @dragover="onDragFocus($event, index)"
          @dragleave="assign.index = null"
          @drop="showAssignPopup"
          @click="showDoctorPopup(index)"
        >
          <tr
            style="cursor:pointer"
            @click="showDoctorPopup(index)"
          >
            <td>{{item.doctor}}</td>
            <td v-if="showAssignColumn">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    @click.stop="showAssignPopupByClick(index)"
                    v-on="on"
                    v-bind="attrs"
                    color="primary"
                  >
                    mdi-plus-circle
                  </v-icon>
                </template>
                <span>Assign</span>
              </v-tooltip>
            </td>
            <td>{{item.specialization}}</td>
            <td :class="statusColor(item.status)" class="text-lowercase">{{item.status}}</td>
            <td>{{item.availability}}</td>
          </tr>
        </drop>
      </template>
    </v-data-table>

    <!-- confirmation popup -->
    <base-confirmation-dialog
      v-model="assign.show"
      @cancel="hideAssignPopup"
      @confirm="confirmAssign"
    >
      <h2
        class="font-weight-regular text-center"
        style="line-height: 130%;"
      >
        Do you want to add this case to 
        <span class="primary--text">
          Dr. {{ selectedDoctor }}
        </span>
        ?
      </h2>
    </base-confirmation-dialog>

    <!-- doctor's detail popup -->
    <v-dialog v-model="doctorDetail.show" max-width="750px">
      <v-card rounded="lg">
        <v-card-title class="primary--text">
          <span>View Details</span>
          <v-icon class="ml-auto" @click="hideDoctorPopup">mdi-close</v-icon>
        </v-card-title>
        <v-card-text>
          <!-- doctors info -->
          <v-row>
            <v-col cols="4">
              <span class="primary--text font-weight-bold text-caption">
                Name
              </span>
              <p class="font-weight-bold">
                {{ doctorDetail.index == null ? '' : filteredDoctors[doctorDetail.index].doctor }}
              </p>
            </v-col>
            <v-col cols="4">
              <span class="primary--text font-weight-bold text-caption">
                Specialization
              </span>
              <p class="">
                {{ doctorDetail.index == null ? '' : filteredDoctors[doctorDetail.index].specialization }}
              </p>
            </v-col>
            <v-col cols="4">
              <span class="primary--text font-weight-bold text-caption">
                Availability
              </span>
              <p class="">
                {{ doctorDetail.index == null ? '' : filteredDoctors[doctorDetail.index].availability }}
              </p>
            </v-col>
          </v-row>
          <!-- assigned IB calls -->
          <span class="primary--text font-weight-bold text-caption">
            Assigned OB calls
          </span>
          <p
            v-if=" doctorDetail.index === null || !filteredDoctors[doctorDetail.index].calls.length"
            class="secondary--text text--darken-4"
          >
            No OB calls yet
          </p>
          <v-list v-else>
            <v-list-item
              v-for="(calls, index) in filteredDoctors[doctorDetail.index].calls"
              :key="index"
            >
              <v-list-item-title>{{ calls.patient.patient.full_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ calls.health_concern }}</v-list-item-subtitle>
              <v-list-item-action>
                <v-icon
                  color="error"
                  @click="showRemoveCasePopup(index)"
                >
                  mdi-delete-outline
                </v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- confirm remove case popup -->
    <base-confirmation-dialog
      v-model="doctorDetail.remove"
      message="Are you sure you want to remove this case?"
      @cancel="hideRemoveCasePopup"
      @confirm="removeCase"
    ></base-confirmation-dialog>
  </div>
</template>

<script>
  import API from '@/api'

  const Consultations = API.get('Consultation')
  const Case = API.get('Cases')

  export default {
    name: 'DoctorsTable',

    components: {
      DoctorsTableFilter: () => import('./DoctorsTableFilter.vue'),
      ResultNumber: () => import('./ResultNumber.vue')
    },

    props: {
      value: { type: Array },
      items: { type: Array },
      showAssignColumn: { type: Boolean, default: false },
      info: { type: Object }
    },

    data() {
      return {
        filteredDoctors: [],
        rowsPerPageItems: [5, 10, 20, -1],
        innerVal: this.value ? this.value : this.items,
        filters: {
          name: null,
          specializations: [],
          status: []
        },
        assign: {
          show: false,
          index: null,
          case: null
        },
        doctorDetail: {
          show: false,
          index: null,
          remove: false,
          caseIndex: null
        },
        itemsTotal: null
      }
    },

    computed: {
      assignId: {
        get() { return this.$store.getters['cases/assignId'] },
        set(val) { return this.$store.commit('cases/setAssignId', val) }
      },
      isAssigning: {
        get() { return this.$store.getters['cases/isAssigning'] },
        set(val) { return this.$store.commit('cases/setAssignLoading', val) }
      },
      headers() {
        var headers = [
          // doctor
          { 
            text: 'Doctor',
            value: 'doctor',
            class:'primary--text',
            width: this.showAssignColumn?'200px':'',
            filter: value => {
              if (!this.filters.name)
                return true
              else
                return value.toLowerCase().includes(this.filters.name.toLowerCase())
            }
          },
          // assign
          { 
            text: '',
            value: 'option',
            width:'20px'
          },
          // specialization
          { 
            text: 'Specialization',
            value: 'specialization',
            class:'primary--text',
            filter: value => {
              if (!this.filters.specializations.length)
                return true
              else
                return this.filters.specializations.includes(value)
            }
          },
          // status
          { 
            text: 'Status',
            value: 'status',
            class:'primary--text',
            filter: value => {
              if (!this.filters.status.length)
                return true
              else
                return this.filters.status.includes(value)
            }
          },
          // availability
          {
            text: 'Availability',
            value: 'availability',
            class:'primary--text'
          }
        ]
        if (!this.showAssignColumn)
          headers.splice(1, 1)

        return headers
      },
      selectedDoctor() {
        var me = this
        var name = ''
        if (me.filters.name) {
          if (me.assign.index !== null) {
            name = me.filteredDoctors[me.assign.index].doctor
          }
        }
        else {
          if (me.assign.index !== null) {
            name = me.filteredDoctors[me.assign.index].doctor
          }
        }
        return name
      }
    },

    
    watch: {
      value(val) {
        this.innerVal = val
      }
    },

    methods: {
      /** assign case popup methods */
      showAssignPopup(draggedCase) {
        if( !draggedCase ) {
          this.assign.index = null
        }
        else {
          this.assign.show = true
          this.assign.case = draggedCase
        }
      },
      showAssignPopupByClick(index=null) {
        if (index !== null) {
          this.assign.index = index
          this.assign.show = true
          this.assign.case = this.info
        }
      },
      hideAssignPopup() {
        this.assign.show = false
        this.assign.index = null
        this.assign.case = null
      },
      async confirmAssign() {
        this.isAssigning = true
        this.assignId = this.assign.case.id
        var data = {}

        console.log({test: this.assign});

        data = {
          doctor_id: this.filteredDoctors[this.assign.index].full.id,
          status: 'ongoing'
        }
        const consultationId = this.assign.case.consultations[0].id
        const consultationResponse = await Consultations.update(consultationId, data)

        console.log('consultationResponse', consultationResponse, consultationId)

        data = {
          doctor_id: this.filteredDoctors[this.assign.index].full.id,
          status: 'ongoing'
        }
        const caseId = this.assign.case.id
        const caseResponse = await Case.update(caseId, data)
        
        if ( consultationResponse.status == 200
        && caseResponse.status == 200 ) {
          this.$store.commit('cases/removeCase', this.assign.case.id);
          this.filteredDoctors[this.assign.index].calls.push(this.assign.case)
          this.$emit('input', this.innerVal)
          this.$emit('newcase', {index:this.assign.index, case:this.assign.case})
          this.hideAssignPopup()
          this.$emit('assigned')
        }

        this.isAssigning = false
      },

      /** doctor's detail popup methods */
      showDoctorPopup(index) {
        this.doctorDetail.index = index
        this.doctorDetail.show = true
      },
      hideDoctorPopup() {
        this.doctorDetail.index = null
        this.doctorDetail.show = false
      },
      /**  doctor's detail popup methods (remove case popup) */
      showRemoveCasePopup(index) {
        this.doctorDetail.remove = true
        this.doctorDetail.caseIndex = index
      },
      hideRemoveCasePopup() {
        this.doctorDetail.remove = true
        this.doctorDetail.caseIndex = null
      },
      removeCase() {
        var info = this.filteredDoctors[this.doctorDetail.index].calls[this.doctorDetail.caseIndex]
        this.filteredDoctors[this.doctorDetail.index].calls.splice(this.doctorDetail.caseIndex, 1)
        // remove doctor id
        this.$store.dispatch('cases/removeAssign', info.id);
        // push to consultations array
        this.$store.commit('cases/pushNewCase', {data: info, index: null});
        this.hideRemoveCasePopup()
      },


      /** HELPERS */

      statusColor(status) {
        switch(status) {
          case 'Online':
            return 'green--text'
          case 'In call':
            return 'orange--text'
          case 'After call work':
            return 'blue--text'
          case 'On break':
            return 'red--text'
          case 'Available':
            return 'green--text'
          case 'Do not disturb':
            return 'red--text'
          case 'End of shift':
            return 'red--text'
          case 'Offline':
            return 'gray--text'
          case 'Away':
            return 'gray--text'
          default:
            return ''
        }
      },
      
      onDragFocus(draggedCase, index) {
        if( !draggedCase ) {
          this.assign.index = null
        }
        else {
          this.assign.index = index;
        }
      }
    },

    
    mounted() {
      var me = this
      this.itemsTotal = this.innerVal.length
      // watches the vuetify data table filtered item
      this.$watch(
        () => 
        // to be watched
        {
          var items = this.$refs.doctorsTable.$children[0].computedItems
          me.filteredDoctors = items
          return items
        },
        // value
        (val) => {
          this.itemsTotal = val.length
        }
      );

      if (this.info) {
        this.assign.case = this.info
      }
    },
  }
</script>

<style lang="scss" scoped>

</style>